/**
 * First-time onboarding tours — the shared FE↔BE vocabulary.
 *
 * Progress is SERVER state owned by Auth (`auth.user_onboarding_progress`),
 * keyed by tenant + user + tour, because a tour is a platform experience and a
 * person expects to see it once, not once per browser. Local state is a cache
 * and never the source of truth.
 */
/**
 * The six independent tours. Independent is the point: completing Home
 * completes nothing else, and opening a SOLVE as owner says nothing about what
 * a collaborator or an inviter has seen.
 *
 * LAMA is deliberately absent — its onboarding has not been scoped.
 */
export const ONBOARDING_TOUR_IDS = [
    "home",
    "solve-owner",
    "solve-collaborator",
    "solve-inviter",
    "vsm",
    "hoshin",
];
/**
 * The read bound for one person's progress rows.
 *
 * The unique key is (tenant, user, tour) and `tour_id` carries a CHECK
 * constraint to the ids above, so the row count is STRUCTURALLY bounded — the
 * headroom is for ids retired from this list while their rows remain, which is
 * the only way the count can exceed it.
 *
 * Deliberately no cursor, and this is the one place to argue about that: a page
 * boundary would be meaningless when the whole set is smaller than any page,
 * and the caller needs every row at once to decide eligibility. The same
 * reasoning `DirectoryGroupRepository.expandDirectoryGroupMembers` records for
 * its own limit-without-cursor.
 */
export const ONBOARDING_TOUR_PROGRESS_LIMIT = 50;
/**
 * `in_progress` is written as soon as a tour STARTS, which is what makes an
 * interrupted tour resumable rather than restartable.
 *
 * `skipped` and `completed` are both terminal for automatic starts. They stay
 * distinct because they answer different questions about the same person, and
 * a future decision about who to re-offer something to needs the difference.
 */
export const ONBOARDING_TOUR_STATUSES = [
    "in_progress",
    "completed",
    "skipped",
];
export function isOnboardingTourId(value) {
    return ONBOARDING_TOUR_IDS.includes(value);
}
// ---------- the shell↔feature occasion seam ----------
/**
 * The closed capability vocabulary a tour step may depend on.
 *
 * Deliberately NOT product roles. A tour must never introduce one, and these
 * are facts a host already holds for its own affordances — so a step that asks
 * "can this person edit" asks the same question the Edit button asks, and
 * cannot drift from it.
 *
 * It lives here, in the shared contract, because THREE packages need the same
 * list: the renderer validates definitions against it, the shell resolves it,
 * and a feature package reports it on an occasion.
 */
export const TOUR_CAPABILITIES = [
    "canEditCanvas",
    "canInviteMembers",
    "canDiscuss",
    "knowledgeStudioEnabled",
    "playerAiEnabled",
    /**
     * This actor can move a VSM room to its next phase (owner or facilitator) —
     * the same `advance_phase` check the room control asks. An affordance, not a
     * role (PB-5441 TRAIN-1).
     */
    "canMoveRoom",
];
/**
 * The name of the browser event a feature package fires when one of its
 * workspaces reaches a state worth onboarding somebody into.
 *
 * A BROWSER EVENT rather than an imported callback, deliberately. The shell
 * composes features and features never import each other or the shell
 * (ADR-0009); a SOLVE workspace must be able to say "I am open, as owner, with
 * these capabilities" without importing the shell's tour host, and the shell
 * must be able to listen without importing SOLVE. The same seam `playerai:open`
 * already uses.
 *
 * The event says WHAT HAPPENED. It never names a tour, never asks for one, and
 * carries no copy — so a feature cannot start a tour the shell has decided is
 * finished, and cannot invent one that does not exist.
 *
 * Only the NAME and the payload SHAPE live here. The dispatch itself is
 * `announceOnboardingOccasion` in `@playbookai/ui`, because this package is
 * platform-neutral: it is compiled without the DOM lib and is imported by
 * `services/*`, where `window` does not exist and must not be asserted to.
 */
export const ONBOARDING_OCCASION_EVENT = "playbookai:onboarding";
export const ONBOARDING_OCCASIONS = [
    "solve-opened-as-owner",
    "solve-opened-as-collaborator",
    "solve-invitation-sent",
    "vsm-opened",
    "hoshin-opened",
];
/**
 * Does a stored record stop this tour starting on its own?
 *
 * ONLY a terminal record does. An absent record is eligibility — for existing
 * users exactly as much as for new ones, because there is no signup-date rule
 * and nothing marks anybody as already onboarded. An `in_progress` record is
 * eligibility too: it resumes.
 *
 * A FAILED or still-loading progress request is not a record and must never
 * reach this function; a tour that starts because a read failed would restart
 * on every retry.
 */
export function blocksAutomaticStart(progress) {
    return progress?.status === "completed" || progress?.status === "skipped";
}
//# sourceMappingURL=onboarding-tour.js.map